import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag, Trash2, Plus, Minus, ArrowRight } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useSettings } from '../../hooks/useSettings';
import { useQuery } from '@tanstack/react-query';

const getTierPriceForQty = (product, qty) => {
  const priceKeys = ['p_1','p_2','p_3','p_4','p_5','p_6','p_7','p_8','p_9','p_10'];
  const labelKeys = ['p1q','p2q','p3q','p4q','p5q','p6q','p7q','p8q','p9q','p10q'];
  let bestPrice = parseFloat(product?.p_1 || 0);
  for (let i = 0; i < 10; i++) {
    const price = parseFloat(product?.[priceKeys[i]] || 0);
    const minQty = parseInt((product?.[labelKeys[i]] || '').replace(/[^0-9]/g, '')) || (i === 0 ? 1 : 0);
    if (price > 0 && minQty > 0 && minQty <= qty) bestPrice = price;
  }
  return bestPrice;
};

const CartDrawer = () => {
  const { isCartOpen, setIsCartOpen, cartItems, cartSubtotal, removeFromCart, updateQuantity } = useCart();
  const { branding, currencySymbol, convertPrice } = useSettings();
  const fmtMoney = (n) => convertPrice(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const primaryBgStyle = { backgroundColor: branding.themeColor || '#1d4ed8' };

  const firstItemCategory = cartItems[0]?.product?.category_name;

  const { data: recommendedProductsResponse = { data: [] } } = useQuery({
    queryKey: ['recommended-products', firstItemCategory],
    queryFn: () => fetch(`/api/products/search?category_name=${encodeURIComponent(firstItemCategory)}&limit=10`).then(res => res.json()),
    enabled: !!firstItemCategory
  });

  const recommendedProducts = recommendedProductsResponse.data || [];

  useEffect(() => {
    const interval = setInterval(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollLeft += 1;
        if (scrollRef.current.scrollLeft >= scrollRef.current.scrollWidth - scrollRef.current.clientWidth) {
          scrollRef.current.scrollLeft = 0;
        }
      }
    }, 30);
    return () => clearInterval(interval);
  }, [recommendedProducts]);

  const handleProductClick = (product) => {
    setIsCartOpen(false);
    navigate(`/products/${product?.id || product?.p_id}`);
  };

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsCartOpen(false)}
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100]"
          />

          {/* Drawer */}
          <motion.div
            data-component="cart-drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-[101] flex flex-col"
          >
            {/* Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ShoppingBag className="text-slate-900" size={24} />
                <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">Your Cart</h2>
                <span className="bg-slate-100 text-slate-500 text-[10px] font-black px-2 py-1 rounded-full">{cartItems.length}</span>
              </div>
              <button
                onClick={() => setIsCartOpen(false)}
                className="p-2 hover:bg-slate-50 rounded-xl transition-all text-slate-400 hover:text-slate-900"
              >
                <X size={24} />
              </button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {cartItems.length > 0 ? (
                cartItems.map((item) => (
                  <motion.div
                    layout
                    key={item.id}
                    className="flex gap-4 group"
                  >
                    <div className="w-24 h-24 bg-slate-50 rounded-2xl overflow-hidden flex-shrink-0 border border-slate-100">
                      {item.product?.image_url && (() => {
                        const variantObj = Array.isArray(item.product.varient)
                          ? item.product.varient.find(v => v?.name === item.details?.variant)
                          : null;
                        const variantImg = variantObj?.images?.[0];
                        const defaultImg = Array.isArray(item.product.image_url) ? item.product.image_url[0] : item.product.image_url;
                        return <img src={variantImg || defaultImg} alt={item.product?.name} className="w-full h-full object-contain p-1.5" />;
                      })()}
                    </div>

                    <div className="flex-1 flex flex-col justify-between py-1">
                      <div className="space-y-1">
                        <div className="flex justify-between items-start gap-2">
                          <h4 
                            onClick={() => handleProductClick(item.product)}
                            className="text-sm font-bold text-slate-900 line-clamp-1 group-hover:text-blue-600 transition-colors cursor-pointer hover:underline"
                          >
                            {item.product?.name}
                          </h4>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            title="Remove from cart"
                            aria-label="Remove from cart"
                            className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-500 hover:text-white transition-all active:scale-95"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-1">
                          {(item.details?.variant || item.details?.tier) && (
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-slate-300">|</span>
                              <p className="text-[10px] font-black text-orange-600 tracking-[0.1em] uppercase bg-orange-50 px-1.5 rounded flex items-center gap-1">
                                {item.details.variant && <span>{typeof item.details.variant === 'object' ? (item.details.variant?.name || '') : item.details.variant}</span>}
                                {item.details.variant && item.details.tier && <span className="opacity-50">•</span>}
                                {item.details.tier && <span>{item.details.tier}</span>}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center bg-slate-50 rounded-xl border border-slate-100 p-1">
                          {(() => {
                            const cartQty = Number(item.details?.quantity || item.qua || item.quantity || 0);
                            const cartMinQty = parseInt((item.product?.p1q || '').replace(/[^0-9]/g, '')) || 1;
                            const cartMaxQty = parseInt(item.product?.quantity || 9999);
                            return (<>
                              <button
                                onClick={() => updateQuantity(item.id, Math.max(cartMinQty, cartQty - 1))}
                                disabled={cartQty <= cartMinQty}
                                className="p-1 hover:bg-white rounded-lg transition-all text-slate-400 disabled:opacity-30 disabled:cursor-not-allowed"
                              >
                                <Minus size={14} />
                              </button>
                              <span className="px-2 text-xs font-black text-slate-900 min-w-[2rem] text-center whitespace-nowrap tabular-nums">{cartQty}</span>
                              <button
                                onClick={() => updateQuantity(item.id, Math.min(cartQty + 1, cartMaxQty))}
                                disabled={cartQty >= cartMaxQty}
                                className="p-1 hover:bg-white rounded-lg transition-all text-slate-400 disabled:opacity-30 disabled:cursor-not-allowed"
                              >
                                <Plus size={14} />
                              </button>
                            </>);
                          })()}
                        </div>
                        <div className="flex items-center gap-1.5">
                          {(() => {
                            const tierIdx = item.details?.tierIndex ?? 0;
                            const tierDiscountKeys = ['p1d','p2d','p3d','p4d','p5d','p6d','p7d','p8d','p9d','p10d'];
                            const tierLabel = String(item.product?.[tierDiscountKeys[tierIdx]] || '').trim();
                            if (tierLabel) {
                              return <span className="text-[9px] font-black text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">{tierLabel}</span>;
                            }
                            return null;
                          })()}
                          <span className="text-sm font-black text-slate-900">
                            {currencySymbol}{(() => {
                              const qty = Number(item.details?.quantity || item.qua || item.quantity || 0);
                              const price = getTierPriceForQty(item.product, qty);
                              return fmtMoney(price * qty);
                            })()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                  <div className="w-20 h-20 bg-slate-50 rounded-[2.5rem] flex items-center justify-center">
                    <ShoppingBag size={32} className="text-slate-200" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-bold text-slate-900 uppercase tracking-tight">Your cart is empty</h3>
                    <p className="text-xs text-slate-400 font-medium">Add some items to get started!</p>
                  </div>
                  <button
                    onClick={() => {
                      setIsCartOpen(false);
                      navigate('/products');
                    }}
                    className="px-6 py-3 bg-slate-900 text-white rounded-xl font-bold text-[10px] uppercase tracking-widest shadow-xl shadow-slate-200"
                    style={primaryBgStyle}
                  >
                    Browse Catalog
                  </button>
                </div>
              )}
            </div>

            {/* Footer */}
            {cartItems.length > 0 && (
              <div className="p-6 bg-slate-50/50 border-t border-slate-100 space-y-6">
                
                {/* Recommended Products */}
                {recommendedProducts.length > 0 && (
                  <div className="space-y-2">
                    <p className="font-bold text-slate-400 uppercase tracking-widest text-[10px]">Recommended Products</p>
                    <div 
                      ref={scrollRef}
                      className="flex gap-4 overflow-x-auto no-scrollbar pb-2"
                    >
                      {recommendedProducts.map(product => (
                        <div 
                          key={product.id || product.p_id} 
                          className="flex-shrink-0 w-28 bg-white p-2 rounded-xl border border-slate-100 cursor-pointer"
                          onClick={() => handleProductClick(product)}
                        >
                          <div className="w-24 h-24 bg-slate-50 rounded-lg overflow-hidden mb-2">
                            <img 
                              src={Array.isArray(product.image_url) ? product.image_url[0] : product.image_url} 
                              alt={product.name} 
                              className="w-full h-full object-contain p-1.5" 
                            />
                          </div>
                          <p className="text-[10px] font-bold text-slate-900 line-clamp-1">{product.name}</p>
                          <p className="text-[10px] font-black text-slate-400">{currencySymbol}{fmtMoney(product.p_1)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="font-bold text-slate-400 uppercase tracking-widest text-[10px]">Subtotal</span>
                    <span className="font-black text-slate-900 text-xl tracking-tighter">{currencySymbol}{fmtMoney(cartSubtotal)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="font-bold text-slate-400 uppercase tracking-widest text-[10px]">Shipping</span>
                    <span className="font-black text-slate-400 text-xs uppercase">Calculated at checkout</span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setIsCartOpen(false);
                    navigate('/checkout');
                  }}
                  className="w-full py-5 rounded-2xl text-white font-black text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-3 hover:translate-y-[-2px] transition-all shadow-2xl shadow-slate-200"
                  style={primaryBgStyle}
                >
                  Proceed to Checkout <ArrowRight size={18} />
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
